// The custom report request form, shown under the dashboard while ?show=true.

import { SelectedFilter, toQuery } from "../api/dashboard";
import { apiHost } from "../config";
import { FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
interface ModalProps {
    url: string
}
interface IDictionary {
    [index: string]: any;
}
const Modal = ({ url }: ModalProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<string[] | null>(null);
    const [success, setSuccess] = useState<boolean>(false)
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    let params = [] as SelectedFilter[];
    searchParams.forEach((value, key) => {
        if (!value || key === "show") {
            return;
        }
        params.push({ name: key, value });
    });
    const close = () => navigate(`${url}?${toQuery(params)}show=false`);

    async function onSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        setIsLoading(true)
        setError(null) // Clear previous errors when a new request starts

        try {
            const formData = new FormData(event.currentTarget);
            formData.append("Filter", JSON.stringify(params));
            const object = {} as IDictionary;
            formData.forEach((value, key) => object[key] = value);

            const response = await fetch(`${apiHost}Dashboard/CustomReport`, {
                method: 'POST',
                body: JSON.stringify(object),
                mode: "cors",
                headers: { "content-type": "application/json" }
            })

            if (!response.ok) {
                if (response.status === 400) {
                    const body = await response.json();
                    let errorList = [] as string[];
                    if (body.errors) {
                        for (const key in body.errors) {
                            errorList.push(body.errors[key]);
                        }
                    } else {
                        errorList.push(body.title);
                    }
                    setError(errorList);
                    return;
                }

                throw new Error('Failed to submit the data. Please try again.');
            }
            if (response.status === 202) {
                setSuccess(true);
            }
        } catch (error) {
            const e = error as Error;
            // Capture the error message to display to the user
            setError([e.message]);
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    if (success) {
        return (
            <div className="fd-request">
                <div className="alert alert-success d-flex align-items-start gap-3 mb-3" role="status">
                    <i className="bi bi-check-circle-fill fs-4" aria-hidden="true" />
                    <div>
                        <strong className="d-block">Request sent</strong>
                        Thank you for your request. You will hear from us shortly.
                    </div>
                </div>
                <button type="button" onClick={close} className="btn btn-outline-primary">Close</button>
            </div>
        );
    }

    return (
        <div className="fd-request">
            <p>
                Tell us your research question. Your current filters are sent with the request. Due to time and budget
                constraints, we may not be able to respond to all requests.
            </p>
            {error && (
                <div className="alert alert-danger" role="alert">
                    {error.length === 1 ? error[0] : <ul className="mb-0">{error.map((e, i) => (<li key={i}>{e}</li>))}</ul>}
                </div>
            )}
            <form onSubmit={onSubmit}>
                <div className="row g-3">
                    <div className="col-md-6">
                        <label htmlFor="fd-first-name" className="form-label">First name</label>
                        <input type="text" name="FirstName" id="fd-first-name" autoComplete="given-name" className="form-control" required />
                    </div>
                    <div className="col-md-6">
                        <label htmlFor="fd-last-name" className="form-label">Last name</label>
                        <input type="text" name="LastName" id="fd-last-name" autoComplete="family-name" className="form-control" required />
                    </div>
                    <div className="col-md-6">
                        <label htmlFor="fd-organization" className="form-label">Organization</label>
                        <input id="fd-organization" name="Organization" type="text" autoComplete="organization" className="form-control" required />
                    </div>
                    <div className="col-md-6">
                        <label htmlFor="fd-title" className="form-label">Job title</label>
                        <input id="fd-title" name="JobTitle" type="text" autoComplete="organization-title" className="form-control" required />
                    </div>
                    <div className="col-12">
                        <label htmlFor="fd-email" className="form-label">Email address</label>
                        <input id="fd-email" name="EmailAddress" type="email" autoComplete="email" className="form-control" required />
                    </div>
                    <div className="col-12">
                        <label htmlFor="fd-notes" className="form-label">Your research question</label>
                        <textarea name="Notes" id="fd-notes" rows={4} className="form-control" aria-describedby="fd-notes-help" required></textarea>
                        <div id="fd-notes-help" className="form-text">Explain how and where the data you are requesting will be used.</div>
                    </div>
                </div>
                <div className="d-flex gap-2 mt-4">
                    <button type="submit" className="btn btn-primary" disabled={isLoading}>
                        {isLoading && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
                        Send request
                    </button>
                    <button type="button" onClick={close} className="btn btn-outline-secondary">Cancel</button>
                </div>
            </form>
        </div>
    );
}
export default Modal;
